import RequestCoverageCard from "@/components/RequestCoverageCard";
import React, { useEffect, useState, useRef } from 'react';
import { Wifi, Map, Zap, List, Loader2, Users, ChevronDown } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getErrorMessage } from '@/utils/errorUtils';

const getWifiMarkerIcon = (theme: string) => {
  const isDark = theme === 'dark';
  const bgColor = isDark ? '#3b82f6' : '#2563eb';
  const html = `
    <div style="background-color: ${bgColor}; color: white; border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); border: 2px solid ${isDark ? '#1f2937' : 'white'};">
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M5 12.55a11 11 0 0 1 14.08 0"/>
        <path d="M1.42 9a16 16 0 0 1 21.16 0"/>
        <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
        <line x1="12" x2="12.01" y1="20" y2="20"/>
      </svg>
    </div>
  `;
  return L.divIcon({
    html,
    className: 'custom-wifi-marker',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16]
  });
};

const isValidLatLng = (lat, lng) =>
  Number.isFinite(lat) &&
  Number.isFinite(lng) &&
  Math.abs(lat) <= 90 &&
  Math.abs(lng) <= 180;

// Returns a stable per-browser device identifier. Generated once on first call,
// then reused. Used to identify this device when connecting to hotspots.
const getDeviceId = (): string => {
  let id = localStorage.getItem('konectx_device_id');
  if (!id) {
    // Use crypto.randomUUID if available, else fall back to a timestamp-based UUID
    id = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : 'dev-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
    localStorage.setItem('konectx_device_id', id);
  }
  return id;
};

const escapeText = (value) =>
  (value || '')
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
import { useApp } from "@/contexts/AppContext";
import { Helmet } from "react-helmet-async";
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/api/client';
import { ENDPOINTS } from '@/api/endpoints';
import {
  searchHotspots,
  connectToHotspot,
  captureAndRecordWitness,
  getHotspotDetails,
  disconnectFromHotspot,
  getMyActiveConnection,
  type EndUserHotspot as Hotspot,
  type HotspotSearchParams,
  type MyActiveConnection
} from '@/api/services/hotspotService';
import FeedbackModal from '@/components/FeedbackModal';
import ActionConfirmationModal from '@/components/ActionConfirmationModal';

export default function KonnectXHotspots() {
  const { theme, user } = useApp();
  const { i18n } = useTranslation();
  const [langOverride, setLangOverride] = useState(
    localStorage.getItem('i18nextLng')?.startsWith('fr') ? 'fr' : 'en'
  );
  const t = i18n.getFixedT(langOverride);
  
  // View and filter states
  const [viewMode, setViewMode] = useState('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const [cityFilter, setCityFilter] = useState('All Cities');
  const [speedFilter, setSpeedFilter] = useState('Any Bandwidth');
  const [statusFilter, setStatusFilter] = useState('All Status');
  
  // Dropdown states
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [showSpeedDropdown, setShowSpeedDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [countryCities, setCountryCities] = useState<string[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  // Backend is now the source of truth for which hotspot the user is connected to.
  const [activeConnection, setActiveConnection] = useState<MyActiveConnection | null>(null);

  // 60-second tick so the 'Connected since … • <duration>' on the active
  // hotspot card stays accurate without a full page refresh. Defined here at
  // the top of the component so the hook count is identical on every render,
  // including the loading-spinner render path.
  const [, setNowTick] = useState<number>(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  // Smart Connect: when on, list is sorted to put the best hotspot first.
  // Persisted in localStorage so the preference survives reloads.
  const [smartConnect, setSmartConnect] = useState<boolean>(() => {
    try {
      return localStorage.getItem('smartConnectEnabled') === '1';
    } catch {
      return false;
    }
  });
  // Kept for any legacy references; UI logic no longer depends on it.
  const [getconnectedhotspotstatus, setconnectedhotspotstatus] = useState(false);
  const [getconnectedhotspot, setconnectedhotspot] = useState<string | null>(null);
  
  const cityDropdownRef = useRef(null);
  const speedDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  
  // Map refs
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersLayerRef = useRef<L.LayerGroup | null>(null);

  // Data states
  const [hotspots, setHotspots] = useState<Hotspot[]>([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0
  });
  const [loading, setLoading] = useState(true);
  const [connectingHotspotId, setConnectingHotspotId] = useState<string | null>(null);
  
  // Modal states
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackData, setFeedbackData] = useState<{
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
  }>({ type: 'success', title: '', message: '' });
  
  const [showConnectionConfirm, setShowConnectionConfirm] = useState(false);
  const [selectedHotspot, setSelectedHotspot] = useState<Hotspot | null>(null);
  const [existingHospot, setExistingHotspot] = useState(null);




  async function fetchExistingHotspot(){
    // let response = await fetch('http://localhost:4000/api/hotspots',{
    //   method: 'get',
    //   headers:{
    //     'Content-Type': 'application/json',
    //   }
    // });

    // let res = await response.json();
    // setExistingHotspot(res);
  }

  const refreshActiveConnection = async () => {
    const conn = await getMyActiveConnection();
    setActiveConnection(conn);
    // Keep legacy values aligned so any stragglers in the codebase still work.
    if (conn && conn.hotspotId) {
      setconnectedhotspot(String(conn.hotspotId));
      setconnectedhotspotstatus(true);
      localStorage.setItem('connectedhotspot', String(conn.hotspotId));
    } else {
      setconnectedhotspot(null);
      setconnectedhotspotstatus(false);
      localStorage.removeItem('connectedhotspot');
    }
  };

  useEffect(() => {
    // Source of truth: ask the backend who the user is connected to.
    refreshActiveConnection();
  }, []);

  // Refresh the active-connection state whenever the user returns to this
  // tab/window. This is what closes the loop after a captive-portal login:
  // the backend creates the Connection record on portal authorize, and the
  // user lands back here — the visibility listener triggers a re-fetch, and
  // the matching hotspot card flips from Connect to Disconnect automatically.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (!document.hidden) {
        refreshActiveConnection();
      }
    };
    const onFocus = () => {
      refreshActiveConnection();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
    };
  }, []);
  
  useEffect(() => {
    const fetchCities = async () => {
      try {
        setCitiesLoading(true);
        const countryRes = await apiClient.get(ENDPOINTS.USER.COUNTRY);
        const countryName = countryRes.data?.data?.country;
        
        if (countryName) {
          const citiesRes = await fetch('https://countriesnow.space/api/v0.1/countries/cities', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ country: countryName })
          });
          const citiesData = await citiesRes.json();
          if (!citiesData.error && citiesData.data) {
            setCountryCities(citiesData.data);
          }
        }
      } catch (error: unknown) {
        console.error('Error fetching cities:', error);
      } finally {
        setCitiesLoading(false);
      }
    };
    
    fetchCities();
  }, []);

  useEffect(() => {
    const storedLang = localStorage.getItem('i18nextLng');
    if (storedLang) {
      const normalized = storedLang.startsWith('fr') ? 'fr' : 'en';
      setLangOverride(normalized);
      if (i18n.language !== normalized) {
        i18n.changeLanguage(normalized);
      }
    }
    const onAppLanguageChanged = (event: Event) => {
      const next = (event as CustomEvent).detail as string;
      const normalized = next?.startsWith('fr') ? 'fr' : 'en';
      setLangOverride(normalized);
    };
    window.addEventListener('appLanguageChanged', onAppLanguageChanged as EventListener);
    return () => {
      window.removeEventListener('appLanguageChanged', onAppLanguageChanged as EventListener);
    };
  }, [i18n]);
  // Fetch hotspots
  useEffect(() => {
    fetchHotspots();
    fetchExistingHotspot();
  }, [searchQuery, cityFilter, speedFilter, statusFilter, pagination?.page]);

  // Map initialization
  useEffect(() => {
    if (viewMode !== 'map') return;
    if (!mapContainerRef.current || mapRef.current) return;

    const mapInstance = L.map(mapContainerRef.current, {
      center: [0, 0],
      zoom: 2,
      zoomControl: true
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(mapInstance);

    const markersLayer = L.layerGroup().addTo(mapInstance);
    mapRef.current = mapInstance;
    markersLayerRef.current = markersLayer;

    return () => {
      mapInstance.remove();
      mapRef.current = null;
      markersLayerRef.current = null;
    };
  }, [viewMode]);

  // Map markers
  useEffect(() => {
    const mapInstance = mapRef.current;
    const markersLayer = markersLayerRef.current;

    if (!mapInstance || !markersLayer) return;

    markersLayer.clearLayers();

    const validHotspots = hotspots.filter(h => {
      const lat = Number(h.location?.coordinates?.lat || h.location?.latitude);
      const lng = Number(h.location?.coordinates?.lng || h.location?.longitude);
      return isValidLatLng(lat, lng) && !(lat === 0 && lng === 0);
    });

    if (validHotspots.length === 0) {
      mapInstance.setView([0, 0], 2);
      return;
    }

    const points = [];

    validHotspots.forEach((hotspot) => {
      const lat = Number(hotspot.location?.coordinates?.lat || hotspot.location?.latitude);
      const lng = Number(hotspot.location?.coordinates?.lng || hotspot.location?.longitude);
      points.push([lat, lng]);

      const marker = L.marker([lat, lng], { icon: getWifiMarkerIcon(theme) });

      const name = escapeText(hotspot.name || t('enduser.hotspot.placeholders.unnamedHotspot', { defaultValue: 'Unnamed Hotspot' }));
      const address = escapeText(hotspot.location?.address || t('enduser.hotspot.placeholders.noAddress', { defaultValue: 'No address' }));
      const city = escapeText(hotspot.location?.city || t('enduser.hotspot.placeholders.noCity', { defaultValue: 'No city' }));
      const status = escapeText(hotspot.status || "unknown");
      const userCountValue = typeof hotspot.userCount === 'number' ? hotspot.userCount : 0;

      // Build popup content
      const popupContent = document.createElement('div');
      popupContent.style.fontSize = '12px';
      popupContent.style.lineHeight = '1.4';
      popupContent.innerHTML = `
        <div style="font-weight:600;font-size:14px;margin-bottom:4px;">${name}</div>
        <div style="color:#666;margin-bottom:4px;">${address}, ${city}</div>
        <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;color:#374151;">
          <span style="font-size:12px;">\u{1F465}</span>
          <span style="font-weight:600;">${userCountValue}</span>
          <span style="color:#6b7280;">${userCountValue === 1 ? t('enduser.hotspot.connections.userConnected', { defaultValue: 'user connected' }) : t('enduser.hotspot.connections.usersConnected', { defaultValue: 'users connected' })}</span>
        </div>
        <div style="display:inline-block;padding:2px 8px;border-radius:12px;font-size:10px;font-weight:bold;background-color:${hotspot.status === 'active' ? '#dcfce7' : '#f3f4f6'};color:${hotspot.status === 'active' ? '#166534' : '#374151'};">${status.toUpperCase()}</div>
      `;

      marker.bindPopup(popupContent);
      marker.on('mouseover', function() {
        marker.openPopup();
      });
      marker.on('mouseout', function() {
        marker.closePopup();
      });
      marker.addTo(markersLayer);
    });

    const mapBounds = L.latLngBounds(points);
    mapInstance.fitBounds(mapBounds, { padding: [20, 20], maxZoom: 13 });
  }, [hotspots, viewMode, t, theme]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
        setShowCityDropdown(false);
      }
      if (speedDropdownRef.current && !speedDropdownRef.current.contains(event.target)) {
        setShowSpeedDropdown(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchHotspots = async () => {
    try {
      setLoading(true);
      
      const params: HotspotSearchParams = {
        search: searchQuery || undefined,
        city: cityFilter,
        speed: speedFilter,
        status: statusFilter,
        page: pagination.page,
        limit: pagination.limit
      };
      
      console.log('Fetching hotspots with params:', params);
      
      const response = await searchHotspots(params);
      console.log('Search hotspots response:', response);
      
      if (response?.data?.hotspots) {
        setHotspots(response.data.hotspots);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        } else {
          setPagination(prev => ({ ...prev, total: response.data.hotspots.length }));
        }
      } else {
        console.warn('No hotspots in response:', response);
        setHotspots([]);
        setPagination(prev => ({ ...prev, total: 0 }));
      }
    } catch (error: unknown) {
      console.error('Error fetching hotspots:', error);
      setFeedbackData({
        type: 'error',
        title: t('enduser.hotspot.errorMessages.loadFailedTitle'),
        message: (error instanceof Error ? error.message : '') || t('enduser.hotspot.errorMessages.loadFailedMessage')
      });
      setShowFeedback(true);
      setHotspots([]);
    } finally {
      setLoading(false);
    }
  };

  // --- distance gating: don't let users "connect" to a hotspot they're far from ---
  const IN_RANGE_KM = 0.15; // 150m, matches mobile kInRangeMeters
  // Capture the user's real browser location once, then compute true distance so we
  // never let someone connect from outside a hotspot's range.
  const [userLoc, setUserLoc] = useState<{ lat: number; lng: number } | null>(null);
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => { console.warn('Geolocation unavailable for distance gate:', err); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, []);

  // Real distance (km) from the user to a hotspot (haversine). null when unknown.
  const distanceKmTo = (h: Hotspot): number | null => {
    if (!userLoc) return null;
    const hlat = Number(h?.location?.coordinates?.lat ?? h?.location?.latitude);
    const hlng = Number(h?.location?.coordinates?.lng ?? h?.location?.longitude);
    if (!Number.isFinite(hlat) || !Number.isFinite(hlng)) return null;
    const toRad = (d: number) => (d * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(hlat - userLoc.lat);
    const dLng = toRad(hlng - userLoc.lng);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(userLoc.lat)) * Math.cos(toRad(hlat)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  // FAIL CLOSED: too far when beyond range OR when distance can't be confirmed.
  // Only a confirmed in-range distance (<= 150m) allows Connect. The active-
  // connection case is handled at the call sites below.
  const isTooFar = (h: Hotspot) => {
    const d = distanceKmTo(h);
    return d === null || d > IN_RANGE_KM;
  };

  // A hotspot is full when its active user count has reached its capacity
  // (maxConnections). New users can't connect — steered to another hotspot.
  const isFull = (h: Hotspot) => {
    const cap = Number(h?.maxConnections) || 0;
    const used = Number(h?.userCount) || 0;
    return cap > 0 && used >= cap;
  };
  const openDirections = (h: Hotspot) => {
    const lat = Number(h?.location?.coordinates?.lat ?? h?.location?.latitude);
    const lng = Number(h?.location?.coordinates?.lng ?? h?.location?.longitude);
    if (!isNaN(lat) && !isNaN(lng)) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
    }
  };

  const handleConnect = (hotspot: Hotspot) => {
    if (isTooFar(hotspot)) { openDirections(hotspot); return; }
    setSelectedHotspot(hotspot);
    setShowConnectionConfirm(true);
  };

  const confirmConnect = async () => {
    if (!selectedHotspot) return;
    
    try {
      setConnectingHotspotId(selectedHotspot.id);
       
      const response = await connectToHotspot(selectedHotspot.id, getDeviceId());
      captureAndRecordWitness(selectedHotspot.id);
      
      localStorage.setItem('connectedhotspot', selectedHotspot.id);
      setconnectedhotspot(localStorage.getItem('connectedhotspot'));
      await refreshActiveConnection();
      setFeedbackData({
        type: 'success',
        title: t('enduser.hotspot.succesModal.title'),
        message: t('enduser.hotspot.succesModal.body', { name: selectedHotspot.name })
      });
      setShowFeedback(true);
      setShowConnectionConfirm(false);
      
      console.log('Connection details:', response.data);
    } catch (error: unknown) {
      console.error('Connection error:', error);
      setShowConnectionConfirm(false);

      try {
        const parsed = JSON.parse(error instanceof Error ? error.message : '');
        const activeConnectionId = parsed?.activeConnectionId;

        if (activeConnectionId) {
          // Auto-disconnect existing connection and retry
          await disconnectFromHotspot(activeConnectionId, activeConnectionId);
          await connectToHotspot(selectedHotspot.id, selectedHotspot.id);
          localStorage.setItem('connectedhotspot', selectedHotspot.id);
          setconnectedhotspot(selectedHotspot.id);
          await refreshActiveConnection();

          setFeedbackData({
            type: 'success',
            title: t('enduser.hotspot.succesModal.title', { defaultValue: 'Connection Successful' }),
            message: t('enduser.hotspot.succesModal.body', { name: selectedHotspot.name, defaultValue: `Successfully connected to ${selectedHotspot.name}` })
          });
          setShowFeedback(true);
        } else {
          throw new Error(parsed?.message || 'Connection failed');
        }
      } catch (retryError: unknown) {
        console.error('Reconnect failed:', retryError);
        setFeedbackData({
          type: 'error',
          title: t('enduser.hotspot.errorModal.title', { defaultValue: 'Connection Failed' }),
          message: t('enduser.hotspot.errorModal.body', { defaultValue: 'Failed to connect to hotspot. Please try again.' })
        });
        setShowFeedback(true);
      }
    } finally {
      setConnectingHotspotId(null);
    }
  };

  const handleViewDetails = async (hotspot: Hotspot) => {
    try {
      const response = await getHotspotDetails(hotspot.id);
      console.log('Hotspot details:', response.data);
      
      setFeedbackData({
        type: 'info',
        title: t('enduser.hotspot.details.title'),
        message: t('enduser.hotspot.details.message', {
          name: response.data.name,
          address: response.data.location.address
        })
      });
      setShowFeedback(true);
    } catch (error: unknown) {
      setFeedbackData({
        type: 'error',
        title: t('enduser.hotspot.errorMessages.detailsFailedTitle'),
        message: (error instanceof Error ? error.message : '') || t('enduser.hotspot.errorMessages.detailsFailedMessage')
      });
      setShowFeedback(true);
    }
  };

  const disconnecthotspot = async (id: string) => {
    try {
      // Use the real connectionId from the backend, not a stale hotspot id.
      const realConnectionId = activeConnection?.connectionId || id;
      const response = await disconnectFromHotspot(id, realConnectionId);
      console.log('Disconnect response:', response);
      localStorage.removeItem('connectedhotspot');
      setconnectedhotspot(null);
      await refreshActiveConnection();
      setFeedbackData({
        type: 'success',
        title: t('enduser.hotspot.disconnectModal.title', { defaultValue: 'Disconnected' }),
        message: t('enduser.hotspot.disconnectModal.body', { defaultValue: 'You have been disconnected from the hotspot.' })
      });
      setShowFeedback(true);
    } catch (err: unknown) {
      console.error('Disconnect error:', err);
      setFeedbackData({
        type: 'error',
        title: t('enduser.hotspot.errorMessages.disconnectFailedTitle', { defaultValue: 'Disconnect Failed' }),
        message: getErrorMessage(err) || 'Failed to disconnect from hotspot. Please try again.'
      });
      setShowFeedback(true);
    }
  };


  const formatDuration = (mins: number | null) => {
    if (mins == null) return '';
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h < 24) return `${h}h ${m}m`;
    const d = Math.floor(h / 24);
    return `${d}d ${h % 24}h`;
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSearchInput('');
    setCityFilter('All Cities');
    setSpeedFilter('Any Bandwidth');
    setStatusFilter('All Status');
    setShowCityDropdown(false);
    setShowSpeedDropdown(false);
    setShowStatusDropdown(false);
  };

  const handleSearchSubmit = () => {
    setSearchQuery(searchInput);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const formatDistance = (distance: number | null | undefined, unit: string | null | undefined) => {
    if (distance === null || distance === undefined) {
      return t('enduser.hotspot.placeholders.notAvailable');
    }
    return `${distance.toFixed(1)} ${unit || 'km'}`;
  };

  if (loading && hotspots.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }


  
  // Format "Connected since 9:47 AM • 2h 13m"
  const formatConnectedSince = (connectedAt: string | Date | undefined): string => {
    if (!connectedAt) return '';
    const start = new Date(connectedAt);
    if (isNaN(start.getTime())) return '';
    const now = new Date();
    const ms = Math.max(0, now.getTime() - start.getTime());
    const totalMin = Math.floor(ms / 60000);
    const hours = Math.floor(totalMin / 60);
    const mins = totalMin % 60;
    const elapsed = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
    const timeStr = start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return `${timeStr} • ${elapsed}`;
  };

  // Pretty 'Connected since 09:47 • 2h 13m' line for the active card.
  // The 60-second tick that re-renders this lives at the top of the
  // component, with the other hooks, to keep hook order stable.
  const formatConnectionInfo = (connectedAt: string | Date | undefined | null): string => {
    if (!connectedAt) return t('enduser.hotspot.connections.connected', { defaultValue: 'Connected' });
    const start = new Date(connectedAt);
    if (isNaN(start.getTime())) return t('enduser.hotspot.connections.connected', { defaultValue: 'Connected' });
    const startTime = start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const elapsedMs = Math.max(0, Date.now() - start.getTime());
    const totalMin = Math.floor(elapsedMs / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    let duration: string;
    if (h > 0) duration = `${h}h ${m}m`;
    else if (m > 0) duration = `${m}m`;
    else duration = '< 1m';
    return t('enduser.hotspot.connections.connectedSince', { startTime, duration, defaultValue: `Connected since ${startTime} • ${duration}` });
  };

  const formatLastActive = (iso?: string | null) => {
    if (!iso) return t('enduser.hotspot.connections.neverActive', { defaultValue: 'no activity yet' });
    const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 1) return t('enduser.hotspot.connections.justNow', { defaultValue: 'just now' });
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  // Smart Connect helpers
  const persistSmartConnect = (value: boolean) => {
    setSmartConnect(value);
    try { localStorage.setItem('smartConnectEnabled', value ? '1' : '0'); } catch { /* ignore */ }
  };

  const computeBestHotspot = (list: Hotspot[]): Hotspot | null => {
    if (!list || list.length === 0) return null;
    const live = list.filter((h) => h.isOnline !== false);
    const onlineActive = (live.length > 0 ? live : list).filter(
      (h) => (h.status || '').toLowerCase() === 'active' || (h.status || '').toLowerCase() === 'online'
    );
    const pool = onlineActive.length > 0 ? onlineActive : (live.length > 0 ? live : list);
    // Prefer nearest when distance is known on at least one hotspot.
    const withDistance = pool.filter(
      (h) => typeof h.distance === 'number' && h.distance !== null
    );
    if (withDistance.length > 0) {
      return [...withDistance].sort((a, b) => (a.distance as number) - (b.distance as number))[0];
    }
    // Otherwise fall back to highest availability (signalStrength).
    return [...pool].sort(
      (a, b) => (b.signalStrength || 0) - (a.signalStrength || 0)
    )[0];
  };

  const recommendedHotspot = smartConnect ? computeBestHotspot(hotspots) : null;
  const displayedHotspots = smartConnect && recommendedHotspot
    ? [recommendedHotspot, ...hotspots.filter((h) => h.id !== recommendedHotspot.id)]
    : hotspots;

  return (
    <>
      <Helmet>
        <title>{t('enduser.hotspot.metaTitle')}</title>
        <meta
          name="description"
          content={t('enduser.hotspot.metaDescription')}
        />
      </Helmet>

      <div className={`min-h-screen ${theme === 'dark' ? 'bg-black' : 'bg-gray-50'}`}>
        <div className="w-full p-4 sm:p-6 md:p-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className={`text-xl sm:text-2xl md:text-3xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {t('enduser.hotspot.headertitle')}
            </h1>
            <p className={`text-base font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('enduser.hotspot.body1')}
            </p>
          </div>

          {/* Smart Connect banner */}
          {/*
          <div className={`mb-6 rounded-2xl border ${
            theme === 'dark'
              ? 'bg-gradient-to-r from-blue-900/30 to-indigo-900/30 border-blue-800'
              : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100'
          }`}>
            <div className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  smartConnect
                    ? 'bg-blue-600 text-white shadow-md'
                    : theme === 'dark' ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-500'
                }`}>
                  <Zap className="w-5 h-5" />
                </div>
                <div>
                  <div className={`text-base font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {t('enduser.hotspot.smartConnect.title', { defaultValue: 'Smart Connect' })}
                  </div>
                  <div className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {smartConnect
                      ? (recommendedHotspot
                          ? `${t('enduser.hotspot.smartConnect.badge', { defaultValue: 'Recommended' })}: ${recommendedHotspot.name}${
                              recommendedHotspot.distance != null
                                ? ` — ${(recommendedHotspot.distance as number).toFixed(1)} ${recommendedHotspot.distanceUnit || 'km'} ${t('enduser.hotspot.connections.away', { defaultValue: 'away' })}`
                                : ''
                            }`
                          : t('enduser.hotspot.smartConnect.noHotspot', { defaultValue: 'No suitable hotspot found right now.' }))
                      : t('enduser.hotspot.smartConnect.description', { defaultValue: 'Let us pick the best hotspot for you. You stay in control — one tap to connect.' })}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 sm:ml-4">
                {smartConnect && recommendedHotspot && activeConnection?.hotspotId !== recommendedHotspot.id && (
                  <button
                    type="button"
                    onClick={() => handleConnect(recommendedHotspot)}
                    className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl shadow-md transition-colors whitespace-nowrap"
                  >
                    {t('enduser.hotspot.smartConnect.connectNow', { defaultValue: 'Connect now' })}
                  </button>
                )}
                <button
                  type="button"
                  role="switch"
                  aria-checked={smartConnect}
                  onClick={() => persistSmartConnect(!smartConnect)}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                    smartConnect ? 'bg-blue-600' : theme === 'dark' ? 'bg-gray-700' : 'bg-gray-300'
                  }`}
                  aria-label="Smart Connect toggle"
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                      smartConnect ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
          */}

       <RequestCoverageCard />
{/* Search and Filters */} 
<div className={`rounded-2xl shadow-lg p-6 mb-8 border ${
  theme === 'dark' ? 'bg-[#333436] border-gray-800' : 'bg-white border-gray-100'
}`}>
  <div className="flex gap-4 mb-5">
    <div className="flex-1">
      <input
        type="text"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        onKeyPress={handleSearchKeyPress}
        placeholder={t('enduser.hotspot.searchinputplaceholder')}
        className={`w-full px-5 py-3 border rounded-xl text-base font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
          theme === 'dark' 
            ? 'bg-[#2a2b2d] border-gray-700 text-white placeholder-gray-500' 
            : 'bg-white border-gray-200 text-gray-900'
        }`}
      />
    </div>
  </div>
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
    {/* City Filter Dropdown */}
    <div className="relative" ref={cityDropdownRef}>
      <button
        onClick={() => setShowCityDropdown(!showCityDropdown)}
        className={`w-full px-5 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium text-left flex items-center justify-between ${
          theme === 'dark' ? 'bg-[#2a2b2d] border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
        }`}
      >
        <span>
          {cityFilter === 'All Cities'
            ? t('enduser.hotspot.allCity.default')
            : cityFilter}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${showCityDropdown ? 'rotate-180' : ''}`} />
      </button>
      {showCityDropdown && (
        <div className={`absolute z-50 w-full mt-2 rounded-xl border shadow-lg overflow-hidden max-h-60 overflow-y-auto ${
          theme === 'dark' ? 'bg-[#2a2b2d] border-gray-700' : 'bg-white border-gray-200'
        }`}>
          {citiesLoading ? (
            <div className="px-5 py-3 text-sm text-center text-gray-500">Loading cities...</div>
          ) : (
            ['All Cities', ...countryCities].map((city) => (
              <button
                key={city}
                onClick={() => {
                  setCityFilter(city);
                  setShowCityDropdown(false);
                }}
                className={`w-full px-5 py-3 text-left text-sm font-medium transition-colors ${
                  cityFilter === city
                    ? theme === 'dark' ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-900'
                    : theme === 'dark' ? 'text-white hover:bg-gray-700' : 'text-gray-900 hover:bg-gray-50'
                }`}
              >
                {city === 'All Cities' ? t('enduser.hotspot.allCity.default') : city}
              </button>
            ))
          )}
        </div>
      )}
    </div>

    {/* Speed Filter Dropdown */}
    <div className="relative" ref={speedDropdownRef}>
      <button
        onClick={() => setShowSpeedDropdown(!showSpeedDropdown)}
        className={`w-full px-5 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium text-left flex items-center justify-between ${
          theme === 'dark' ? 'bg-[#2a2b2d] border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
        }`}
      >
        <span>
          {speedFilter === 'Any Bandwidth'
            ? t('enduser.hotspot.anyspeed.default')
            : speedFilter}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${showSpeedDropdown ? 'rotate-180' : ''}`} />
      </button>
      {showSpeedDropdown && (
        <div className={`absolute z-50 w-full mt-2 rounded-xl border shadow-lg overflow-hidden ${
          theme === 'dark' ? 'bg-[#2a2b2d] border-gray-700' : 'bg-white border-gray-200'
        }`}>
          {['Any Bandwidth', '50+ Mbps', '100+ Mbps', '150+ Mbps'].map((speed) => (
            <button
              key={speed}
              onClick={() => {
                setSpeedFilter(speed);
                setShowSpeedDropdown(false);
              }}
              className={`w-full px-5 py-3 text-left text-sm font-medium transition-colors ${
                speedFilter === speed
                  ? theme === 'dark' ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-900'
                  : theme === 'dark' ? 'text-white hover:bg-gray-700' : 'text-gray-900 hover:bg-gray-50'
              }`}
            >
              {speed === 'Any Bandwidth' ? t('enduser.hotspot.anyspeed.default') : speed}
            </button>
          ))}
        </div>
      )}
    </div>

    {/* Status Filter Dropdown */}
    <div className="relative" ref={statusDropdownRef}>
      <button
        onClick={() => setShowStatusDropdown(!showStatusDropdown)}
        className={`w-full px-5 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium text-left flex items-center justify-between ${
          theme === 'dark' ? 'bg-[#2a2b2d] border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
        }`}
      >
        <span>
          {statusFilter === 'All Status'
            ? t('enduser.hotspot.allstatus.default')
            : statusFilter === 'active'
            ? t('enduser.hotspot.allstatus.others.0')
            : statusFilter === 'inactive'
            ? t('enduser.hotspot.allstatus.others.1')
            : statusFilter}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`} />
      </button>
      {showStatusDropdown && (
        <div className={`absolute z-50 w-full mt-2 rounded-xl border shadow-lg overflow-hidden ${
          theme === 'dark' ? 'bg-[#2a2b2d] border-gray-700' : 'bg-white border-gray-200'
        }`}>
          {['All Status', 'active', 'inactive'].map((status) => (
            <button
              key={status}
              onClick={() => {
                setStatusFilter(status);
                setShowStatusDropdown(false);
              }}
              className={`w-full px-5 py-3 text-left text-sm font-medium transition-colors ${
                statusFilter === status
                  ? theme === 'dark' ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-900'
                  : theme === 'dark' ? 'text-white hover:bg-gray-700' : 'text-gray-900 hover:bg-gray-50'
              }`}
            >
              {status === 'All Status'
                ? t('enduser.hotspot.allstatus.default')
                : status === 'active'
                ? t('enduser.hotspot.allstatus.others.0')
                : t('enduser.hotspot.allstatus.others.1')}
            </button>
          ))}
        </div>
      )}
    </div>

    <button 
      onClick={handleClearFilters}
      className={`w-full px-5 py-3 border rounded-xl text-sm font-semibold hover:bg-gray-100 dark:hover:bg-gray-800 transition ${
        theme === 'dark' ? 'bg-[#2a2b2d] border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
      }`}
    >
      {t('enduser.hotspot.clearfilters')}
    </button>
  </div>
</div>





{/* Results Header */}
<div className="mb-6">
  <div className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
    {t('enduser.hotspot.body2.0')} {pagination.total} {t('enduser.hotspot.body2.1')}
  </div>
  
  {/* View Toggle Container */}
  <div className={`inline-flex rounded-xl p-1 ${theme === 'dark' ? 'bg-[#333436]' : 'bg-gray-100'}`}>
   <button
  onClick={() => setViewMode('list')}
  className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition ${
    viewMode === 'list'
      ? theme === 'dark'
        ? 'bg-gray-600 text-white'
        : 'bg-white text-gray-600 shadow-sm'
      : theme === 'dark'
        ? 'text-gray-400 hover:text-white'
        : 'text-gray-600 hover:text-gray-900'
  }`}
>
  <List className="w-5 h-5" />
  <span>{t('enduser.hotspot.listview')}</span>
</button>

    <button
  onClick={() => setViewMode('map')}
  className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition ${
    viewMode === 'map'
      ? theme === 'dark'
        ? 'bg-gray-600 text-white'
        : 'bg-white text-gray-600 shadow-sm'
      : theme === 'dark'
        ? 'text-gray-400 hover:text-white'
        : 'text-gray-600 hover:text-gray-900'
  }`}
>
  <Map className="w-5 h-5" />
  <span>{t('enduser.hotspot.mapview')}</span>
</button>

  </div>
</div>

{/* Loading State */}
{loading ? (
  <div className="flex justify-center items-center py-12">
    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
  </div>
) : hotspots.length === 0 ? (
  <div className={`text-center py-16 rounded-2xl border ${
    theme === 'dark' ? 'bg-[#333436] border-gray-800' : 'bg-white border-gray-100'
  }`}>
    <div className={`w-20 h-20 rounded-full ${theme === 'dark' ? 'bg-blue-500/10' : 'bg-blue-50'} flex items-center justify-center mx-auto mb-4`}>
      <Wifi className={`w-10 h-10 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'} opacity-50`} />
    </div>
    <p className={`text-lg font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
      {t('enduser.hotspot.connections.errorMessages.no_hotspot_found')}
    </p>
    <p className={`text-base font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
      {t('enduser.hotspot.connections.errorMessages.try_adjust_filters')}
    </p>
  </div>
) : viewMode === 'map' ? (
  /* Map View */
  <div className={`rounded-2xl border p-4 sm:p-6 ${
    theme === 'dark' ? 'bg-[#333436] border-gray-800' : 'bg-white border-gray-100'
  }`}>
    <div className="flex items-center justify-between mb-6">
      <div>
        <h3 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          {t('enduser.hotspot.mapviewTitle')}
        </h3>
        <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          {t('enduser.hotspot.mapviewDesc', { count: hotspots.length })}
        </p>
      </div>
    </div>
    <div className="relative z-0 w-full h-[300px] sm:h-[400px] md:h-[500px] rounded-xl overflow-hidden shadow-inner bg-gray-100 dark:bg-gray-800">
      <div
        ref={mapContainerRef}
        className="w-full h-full"
        style={{ zIndex: 0 }}
      />
      {hotspots.filter(h => isValidLatLng(Number(h.location?.coordinates?.lat || h.location?.latitude), Number(h.location?.coordinates?.lng || h.location?.longitude))).length === 0 && (
        <div className={`absolute inset-0 z-10 flex items-center justify-center bg-black/5 backdrop-blur-[2px] ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
          <div className="flex flex-col items-center bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700">
            <Map className="w-12 h-12 mb-4 opacity-50 text-blue-500" />
            <p className="font-semibold text-center max-w-[250px]">
              {t('enduser.hotspot.mapview.noCoordinates', { defaultValue: 'No hotspots with valid coordinates to display on the map.' })}
            </p>
          </div>
        </div>
      )}
    </div>
  </div>
) : (
  /* Hotspot Cards */
  <div className="space-y-5">
    {displayedHotspots.map((hotspot) => (
      <div 
        key={hotspot.id} 
        className={`w-full rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow border relative ${
          activeConnection?.hotspotId === hotspot.id
            ? (theme === 'dark' ? 'bg-green-900/10 border-green-500 ring-2 ring-green-500/40' : 'bg-green-50/40 border-green-400 ring-2 ring-green-300/50')
            : smartConnect && recommendedHotspot && recommendedHotspot.id === hotspot.id
              ? (theme === 'dark' ? 'bg-[#333436] border-blue-500 ring-2 ring-blue-500/40' : 'bg-white border-blue-400 ring-2 ring-blue-300/40')
              : (theme === 'dark' ? 'bg-[#333436] border-gray-800' : 'bg-white border-gray-100')
        }`}
      >
        {smartConnect && recommendedHotspot && recommendedHotspot.id === hotspot.id && (
          <span className="absolute -top-3 left-6 inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded-full text-xs font-bold shadow-md">
            <Zap className="w-3.5 h-3.5" /> {t('enduser.hotspot.smartConnect.badge', { defaultValue: 'Recommended' })}
          </span>
        )}
        {activeConnection?.hotspotId === hotspot.id && (
          <div className={`mb-4 px-3 py-2 rounded-lg flex items-center gap-2 ${
            theme === 'dark' ? 'bg-green-900/20 border border-green-800/50' : 'bg-green-50 border border-green-200'
          }`}>
            <span className="relative flex w-2.5 h-2.5">
              <span className="absolute inline-flex w-full h-full bg-green-400 rounded-full opacity-75 animate-ping"></span>
              <span className="relative inline-flex w-2.5 h-2.5 bg-green-500 rounded-full"></span>
            </span>
            <span className={`text-sm font-semibold ${theme === 'dark' ? 'text-green-300' : 'text-green-700'}`}>
              Connected since {formatConnectedSince(activeConnection.connectedAt)}
            </span>
          </div>
        )}
        <div className="flex flex-col sm:flex-row items-start justify-between gap-4 sm:gap-0">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {hotspot.name || t('enduser.hotspot.placeholders.unnamedHotspot')}
              </h3>
              {hotspot.isNew && (
                <span className="px-3 py-1 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400">
                  New
                </span>
              )}
              <span className={`px-3 py-1 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 ${
                hotspot.isOnline !== false
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${hotspot.isOnline !== false ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                {hotspot.isOnline !== false
                  ? t('enduser.hotspot.connections.onlineBadge', { defaultValue: 'Online' })
                  : t('enduser.hotspot.connections.offlineBadge', { defaultValue: 'Offline' })}
              </span>
            </div>
            <div className={`text-base mb-5 font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              {hotspot.location?.address || t('enduser.hotspot.placeholders.noAddress')}, {hotspot.location?.city || t('enduser.hotspot.placeholders.noCity')}
            </div>
            {hotspot.isOnline === false && (
              <div className={`-mt-3 mb-5 text-sm font-medium ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                {t('enduser.hotspot.connections.lastActive', { defaultValue: 'Last active' })}: {formatLastActive(hotspot.lastSeen)}
              </div>
            )}
            
            <div className={`flex items-center gap-6 text-sm mb-5 flex-wrap ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              <div className="flex items-center gap-2">
                <Map className="w-5 h-5 text-gray-500" />
                <span className="font-bold">{formatDistance(distanceKmTo(hotspot), 'km')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-gray-500" />
                <span className="font-bold">{hotspot.speed || 0} {hotspot.speedUnit || 'Mbps'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-gray-500" />
                <span className="font-bold">{hotspot.userCount || 0}/{hotspot.maxConnections || 50} {t('enduser.hotspot.connections.user')}</span>
              </div>
            </div>

            <div>
              <div className={`flex items-center justify-between text-sm font-bold mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                <span>{t('enduser.hotspot.connections.networkstrenght', { defaultValue: 'Availability' })}</span>
                <span>{hotspot.signalStrength || 0}%</span>
              </div>
              <div className={`w-full rounded-full h-2.5 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}>
                <div
                  className={`h-2.5 rounded-full transition-all ${
                    (hotspot.signalStrength || 0) >= 80 
                      ? 'bg-blue-600'
                      : (hotspot.signalStrength || 0) >= 50
                      ? 'bg-yellow-600'
                      : 'bg-red-600'
                  }`}
                  style={{ width: `${hotspot.signalStrength || 0}%` }}
                />
              </div>
            </div>
          </div>

                    <div className="flex flex-col sm:flex-row sm:flex-col gap-2 sm:gap-3 w-full sm:w-auto sm:ml-8">
                      {isFull(hotspot) && activeConnection?.hotspotId !== hotspot.id && (
                        <div className={`text-xs font-medium text-center px-3 py-2 rounded-lg ${theme === 'dark' ? 'bg-gray-800 text-red-400' : 'bg-red-50 text-red-700'}`}>
                          {t('enduser.hotspot.connections.full', { defaultValue: 'Hotspot full — try another hotspot' })}
                        </div>
                      )}
                      {isTooFar(hotspot) && activeConnection?.hotspotId !== hotspot.id && (
                        <div className={`text-xs font-medium text-center px-3 py-2 rounded-lg ${theme === 'dark' ? 'bg-gray-800 text-amber-400' : 'bg-amber-50 text-amber-700'}`}>
                          {t('enduser.hotspot.connections.tooFar', { defaultValue: 'Too far to connect — go to this location' })}
                        </div>
                      )}
                      {isTooFar(hotspot) && activeConnection?.hotspotId !== hotspot.id ? (
                        <button
                          type="button"
                          onClick={() => openDirections(hotspot)}
                          className="px-4 sm:px-8 py-2.5 sm:py-3.5 border-2 border-blue-600 text-blue-600 dark:text-blue-400 rounded-xl font-medium text-sm sm:text-base transition-colors hover:bg-blue-50 dark:hover:bg-blue-950 flex items-center justify-center gap-2"
                        >
                          <Map className="w-4 h-4" />
                          {t('enduser.hotspot.connections.directions', { defaultValue: 'Directions' })}
                        </button>
                      ) : (
                      // Connect button commented out — connections now happen via captive portal / Wi-Fi join
                      // Original connect button code preserved below for reference:
                      // <button onClick={...} disabled={...} className="...">
                      //   {connectingHotspotId === hotspot.id ? <Loader2 /> : activeConnection?.hotspotId === hotspot.id ? 'Disconnect' : 'Connect'}
                      // </button>
                      null
                      )}
                      <button 
                        onClick={() => handleViewDetails(hotspot)}
                        className={`px-4 sm:px-8 py-2.5 sm:py-3.5 border-2 rounded-xl font-medium text-sm sm:text-base transition-colors ${
                          theme === 'dark' 
                            ? 'border-gray-700 text-gray-300 hover:bg-gray-700' 
                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {t('enduser.hotspot.connections.detailbutton')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-8">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page === 1}
                className={`px-5 py-2.5 rounded-xl font-semibold text-sm transition shadow-sm ${
                  theme === 'dark'
                    ? 'bg-[#333436] text-white hover:bg-gray-700 disabled:opacity-50'
                    : 'bg-white text-gray-900 hover:bg-gray-50 disabled:opacity-50 border border-gray-200'
                } disabled:cursor-not-allowed`}
              >
                {t('enduser.hotspot.pagination.previous')}
              </button>
              <span className={`font-semibold text-base ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('enduser.hotspot.pagination.page')} {pagination.page} {t('enduser.hotspot.pagination.of')} {pagination.totalPages}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                disabled={pagination.page === pagination.totalPages}
                className={`px-5 py-2.5 rounded-xl font-semibold text-sm transition shadow-sm ${
                  theme === 'dark'
                    ? 'bg-[#333436] text-white hover:bg-gray-700 disabled:opacity-50'
                    : 'bg-white text-gray-900 hover:bg-gray-50 disabled:opacity-50 border border-gray-200'
                } disabled:cursor-not-allowed`}
              >
                {t('enduser.hotspot.pagination.next')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedback}
        onClose={() => setShowFeedback(false)}
        type={feedbackData.type}
        title={feedbackData.title}
        message={feedbackData.message}
      />

      {/* Connection Confirmation Modal */}
      <ActionConfirmationModal
        isOpen={showConnectionConfirm}
        onClose={() => setShowConnectionConfirm(false)}
        onConfirm={confirmConnect}
        title={t('enduser.hotspot.connectionmodal.title')}
        message={
          selectedHotspot
            ? t('enduser.hotspot.connectionmodal.body', { name: selectedHotspot.name || t('enduser.hotspot.connectionmodal.fallbackName') })
            : t('enduser.hotspot.connectionmodal.bodyFallback')
        }
        itemName={selectedHotspot?.name}
        additionalInfo={
          selectedHotspot
            ? `${selectedHotspot.location?.address || t('enduser.hotspot.placeholders.unknownLocation')} • ${selectedHotspot.speed || 0} ${selectedHotspot.speedUnit || 'Mbps'} • ${formatDistance(selectedHotspot.distance, selectedHotspot.distanceUnit)}`
            : undefined
        }
        confirmText={t('enduser.hotspot.connectionmodal.connectbutton')}
        variant="info"
        isLoading={connectingHotspotId !== null}
      />



    </>
  );
}
